import React, { Dispatch, useEffect, useState } from 'react';
import { Modal } from 'antd';
import {
  getAdminNewsList,
  deleteNews,
  editNews,
  createNews,
} from '../../../api';
import { useDispatch, useSelector } from 'react-redux';
import styles from './index.module.scss';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../../store/modules/common';
import { SystemMenuId } from '../../../model/system/menu';
import getFormatMessage from '../../../locales/get-format-message';
import {
  getCurrentLangText,
  getLocalTime,
  getNeedManageSites,
  getSiteOptions,
  getSiteText,
  isJSONString,
  isMultiManageSite,
  sortSites,
} from '../../../lib';
import { EditModal, MODULE_OPTIONS } from './edit';
import { messageError, messageSuccess } from '../../../components/message';

import { getStatusText } from '../../lib';
import {
  CommonStatusStep,
  INews,
  ISupportUpgradeNews,
} from '../../../model/api-modules';
import classnames from 'classnames';
import { NewsModule } from '../../../model';
import { pick } from 'lodash';
import { AutoAntTable } from '../../../components/auto-ant-table';
import { wrapRequestArea } from '../../../lib/http/lib';
import {
  CommonAddButton,
  CommonButton,
  CommonButtonGroup,
} from '../../../components/button/main';
import { IFilterItem } from '../../../components/filter-group';
import { shortHash10 } from '../lib/hash';
import { IRootState } from '../../../store/reducers';

export const AdminUpgradeNotification = () => {
  const [newsList, setNewsList] = useState<ISupportUpgradeNews[]>([]);
  const [newsListLoading, setNewsListLoading] = useState<boolean>(false);
  const [deleteNewsLoading, setDeleteNewsLoading] = useState<boolean>(false);
  const [showNewsModal, setShowNewsModal] = useState<boolean>(false);
  const [currentRecord, setCurrentRecord] =
    useState<ISupportUpgradeNews | null>(null);
  const [showDeleteNewsModal, setShowDeleteNewsModal] =
    useState<boolean>(false);
  const [deleteRecord, setDeleteRecord] = useState<ISupportUpgradeNews | null>(
    null
  );
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  const menuDispatch = useDispatch<Dispatch<CommonAction>>();

  menuDispatch({
    type: ReduxCommonActionType.SELECT_MENU_ID,
    result: SystemMenuId.UpgradeNotification,
  });

  useEffect(() => {
    initList();
  }, []);
  const initList = async () => {
    setNewsListLoading(true);

    const allSiteRes: Record<string, INews[]> = {};
    await Promise.all(
      getNeedManageSites().map(async (site) => {
        const siteRes = await wrapRequestArea(
          () =>
            getAdminNewsList({
              Module: [
                NewsModule.UpgradeDevPortal,
                NewsModule.UpgradeDocumentation,
                NewsModule.UpgradeDownloadCenter,
                NewsModule.UpgradeWebsite,
                NewsModule.UpgradeUserCenter,
              ].join(),
            }),
          site
        );

        allSiteRes[site] = siteRes;
      })
    );

    const allSiteList = Object.keys(allSiteRes)
      .map((site) => {
        return allSiteRes[site]?.map((item) => {
          return { ...item, site };
        });
      })
      .flat()
      .sort((a, b) => (a.ModifyTime < b.ModifyTime ? 1 : -1));

    const combineList = allSiteList.reduce<ISupportUpgradeNews[]>(
      (acc, listItem) => {
        if (!listItem.DataSyncID) {
          acc.push(listItem);
          return acc;
        }
        if (acc.some((item) => item.DataSyncID === listItem.DataSyncID)) {
          return acc;
        }

        const itemAllSiteRecords = allSiteList.filter(
          (item) => item.DataSyncID === listItem.DataSyncID
        );
        const newListItem = {
          ...listItem,
          allSiteRecord: itemAllSiteRecords.length
            ? itemAllSiteRecords
            : undefined,
        };
        acc.push(newListItem);
        return acc;
      },
      []
    );

    setNewsList(combineList);

    setNewsListLoading(false);
  };

  const COLUMNS = [
    {
      title: getFormatMessage('name_uppercase'),
      dataIndex: 'Title',
      key: 'Title',
      render: (text: string) => {
        return getCurrentLangText(text);
      },
    },
    {
      title: getFormatMessage('status'),
      dataIndex: 'Status',
      key: 'Status',
      render: (_text: any, record: INews) => {
        return (
          <div style={{ display: 'flex' }}>
            {record.Status === CommonStatusStep.Resolve ? (
              <div className={classnames(styles.dot, styles.active)}></div>
            ) : (
              <div className={classnames(styles.dot, styles.pending)}> </div>
            )}
            <div>{getStatusText(record.Status)}</div>
          </div>
        );
      },
    },
    {
      title: getFormatMessage('news_range'),
      dataIndex: 'Module',
      key: 'Module',
      render: (text: any) => {
        return MODULE_OPTIONS.find((item) => item.value === text)?.label;
      },
    },
    {
      title: getFormatMessage('发布站点'),
      dataIndex: 'site',
      key: 'site',
      render: (text: string, record: ISupportUpgradeNews) => {
        return (
          sortSites(
            record.allSiteRecord?.map((item) => item.site || '').filter(Boolean)
          )
            ?.map((site) => getSiteText(site))
            .join('/') || getSiteText(record.site)
        );
      },
    },
    {
      title: getFormatMessage('active_time'),
      dataIndex: 'activeTime',
      key: 'ActiveTime',
      width: 200,
      render: (text: any, record: INews) => {
        const descObj =
          (isJSONString(record.Description) &&
            JSON.parse(record.Description)) ||
          {};
        if (!descObj.ActiveTime) {
          return '';
        }
        return getLocalTime(
          descObj.ActiveTime,
          userInfo?.UserSettings?.Timezone
        );
      },
    },
    {
      title: getFormatMessage('expired_time'),
      dataIndex: 'ExpiredTime',
      key: 'ExpiredTime',
      width: 200,
      render: (text: any, record: INews) => {
        const descObj =
          (isJSONString(record.Description) &&
            JSON.parse(record.Description)) ||
          {};
        if (!descObj.ExpiredTime) {
          return '';
        }
        return getLocalTime(
          descObj.ExpiredTime,
          userInfo?.UserSettings?.Timezone
        );
      },
    },
    {
      title: getFormatMessage('operate'),
      key: 'Operate',
      render: (_text: string, record: ISupportUpgradeNews) => {
        return renderDefaultOperate(
          record,
          handleEdit,
          deleteNewsHandler,
          copyHandler
        );
      },
    },
  ];

  const renderDefaultOperate = (
    record: ISupportUpgradeNews,
    onEdit: (record: INews) => void,
    onDel: (record: INews) => void,
    onCopy: (record: ISupportUpgradeNews) => void
  ): React.ReactNode => {
    const isEnable = record.Status === CommonStatusStep.Resolve;
    const handleStatusUpdate = async () => {
      const descObj =
        (isJSONString(record.Description) && JSON.parse(record.Description)) ||
        {};
      const saveTimeKey = record.Status ? 'ExpiredTime' : 'ActiveTime';
      const unsavedKey = record.Status ? 'ActiveTime' : 'ExpiredTime';
      const savedDescObj = {
        [saveTimeKey]: new Date().getTime(),
        [unsavedKey]: descObj[unsavedKey] || 0,
      };

      const handleRecords = record.allSiteRecord
        ? record.allSiteRecord
        : [record];

      const res = await Promise.all(
        handleRecords.map((record) =>
          wrapRequestArea(
            () =>
              editNews(
                {
                  Status: Number(!record.Status),
                  Description: JSON.stringify(savedDescObj),
                },
                record.id
              ),
            record.site
          )
        )
      );

      if (res.every((item) => item === 0)) {
        messageSuccess(getFormatMessage('操作成功'));
        initList();
        return;
      }
      messageSuccess(getFormatMessage('操作失败'));
      initList();
    };

    return (
      <CommonButtonGroup>
        <CommonButton type="link" onClick={handleStatusUpdate}>
          {isEnable
            ? getFormatMessage('common_disable')
            : getFormatMessage('common_enable')}
        </CommonButton>

        <CommonButton type="link" onClick={() => onEdit(record)}>
          {isNewsActived(record)
            ? getFormatMessage('view')
            : getFormatMessage('edit')}
        </CommonButton>

        <CommonButton type="link" onClick={() => onCopy(record)}>
          {getFormatMessage('copy')}
        </CommonButton>

        <CommonButton
          type="link"
          className="ant-btn-dangerous"
          onClick={() => onDel(record)}
        >
          {getFormatMessage('delete')}
        </CommonButton>
      </CommonButtonGroup>
    );
  };

  const deleteNewsHandler = (record: ISupportUpgradeNews) => {
    setShowDeleteNewsModal(true);
    setDeleteRecord(record);
  };

  const copyHandler = (record: ISupportUpgradeNews) => {
    if (!record) {
      return;
    }
    Modal.confirm({
      content: getFormatMessage('is_sure_copy_record'),
      onOk: async () => {
        const params = pick({ ...record, Status: 0, Description: '' }, [
          'Description',
          'Module',
          'StudioID',
          'Title',
          'Content',
          'Status',
        ]);
        const hashStr = await shortHash10(
          `${params.Title}${params.Module}${new Date().getTime()}`
        );

        const handleRecords = record.allSiteRecord
          ? record.allSiteRecord
          : [record];

        await Promise.all(
          handleRecords.map((item) =>
            wrapRequestArea(
              () => createNews({ ...params, DataSyncID: hashStr }),
              item.site
            )
          )
        );

        initList();
      },
    });
  };

  const handleEdit = (record: ISupportUpgradeNews) => {
    setShowNewsModal(true);
    setCurrentRecord(record);
  };

  const onDeleteCancel = () => {
    setShowDeleteNewsModal(false);
    setDeleteRecord(null);
  };

  const onDeleteNews = async () => {
    if (deleteRecord) {
      setDeleteNewsLoading(true);
      const deletedRecords = deleteRecord.allSiteRecord
        ? deleteRecord.allSiteRecord
        : [deleteRecord];

      const res = await Promise.all(
        deletedRecords.map((record) =>
          wrapRequestArea(() => deleteNews(record?.id), record.site)
        )
      );

      setDeleteNewsLoading(false);
      if (res.every((item) => item === 0)) {
        messageSuccess(getFormatMessage('del_success'));
        onDeleteCancel();
        initList();
        return;
      }
      messageError(getFormatMessage('error_delete'));
    }
  };

  const renderDeleteNewsModal = () => {
    return (
      <Modal
        open={showDeleteNewsModal && !!deleteRecord}
        title={getFormatMessage('delete')}
        onCancel={onDeleteCancel}
        maskClosable={false}
        onOk={onDeleteNews}
        okText={getFormatMessage('delete')}
        confirmLoading={deleteNewsLoading}
      >
        <div>{`${getFormatMessage('del_news_sure_desc')}${getCurrentLangText(
          deleteRecord?.Title || ''
        )}`}</div>
      </Modal>
    );
  };

  const isNewsActived = (currentRecord?: INews | null) => {
    const descObj =
      (currentRecord &&
        isJSONString(currentRecord.Description) &&
        JSON.parse(currentRecord.Description)) ||
      {};
    return !!descObj.ActiveTime;
  };

  const renderNewsModal = () => {
    if (showNewsModal) {
      const isActived = isNewsActived(currentRecord);
      return (
        <EditModal
          setShowNewsModal={setShowNewsModal}
          onOk={initList}
          currentRecord={currentRecord}
          setCurrentRecord={setCurrentRecord}
          viewOnly={isActived}
        />
      );
    }
    return null;
  };

  const clickCreate = () => {
    setShowNewsModal(true);
    setCurrentRecord(null);
  };
  const filters: IFilterItem<ISupportUpgradeNews>[] = [
    {
      name: 'create',
      render: () => {
        return (
          <CommonAddButton onClick={clickCreate}>
            {getFormatMessage('创建通知')}
          </CommonAddButton>
        );
      },
    },
  ];

  if (isMultiManageSite()) {
    filters.push({
      label: '发布站点',
      name: 'site',
      type: 'select',
      options: getSiteOptions(),
      filter: (val, record) => {
        return (
          !!record.allSiteRecord?.some((item) => item.site === val) ||
          record.site === val
        );
      },
    });
  }
  return (
    <div className={styles.news}>
      <AutoAntTable
        loading={newsListLoading}
        dataSource={newsList}
        columns={COLUMNS}
        configFilter={{
          filters,
        }}
      />
      {renderNewsModal()}
      {renderDeleteNewsModal()}
    </div>
  );
};
